import {
  Calculate as CalculateIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Box,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Dayjs } from 'dayjs';
import { getCreditLineByProduct } from 'lib/api/creditLine';
import AlertBanner from 'lib/designSystem/AlertBanner';
import riskPremiumCalculator, {
  RiskPremiumRates,
} from 'lib/functions/riskPremiumCalculator';
import { Output } from 'lib/functions/riskPremiumCalculator';
import { formatMoney, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import RatesCollapse from './Components/RatesCollapse';
import ReverseCalculationPanel from './Components/ReverseCalculationPanel';

interface Props {
  amount: number;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  quotedCurrency: string;
  type: string;
  businessIdentifier: string | null;
  rateControls: RiskPremiumRates;
  setRateControls: React.Dispatch<React.SetStateAction<RiskPremiumRates>>;
  useConcentrationRates: boolean;
  canEditRates: boolean;
  isCreating?: boolean;
}

type CalculationResult = Output;

const Calculator: React.FC<Props> = ({
  amount,
  startDate,
  endDate,
  quotedCurrency,
  type,
  businessIdentifier,
  rateControls,
  setRateControls,
  useConcentrationRates,
  canEditRates = false,
  isCreating = false,
}) => {
  const dispatch = useDispatch();
  const { calculate } = riskPremiumCalculator();
  const [showRateControls, setShowRateControls] = useState(false);
  const [calculationResult, setCalculationResult] =
    useState<CalculationResult | null>(null);
  const { data, isPending } = getCreditLineByProduct(
    dispatch,
    businessIdentifier,
    'sgr',
  );

  useEffect(() => {
    if (!useConcentrationRates) return;
    const subProductsData = data?.subProducts || [];
    if (subProductsData.length > 0 && type) {
      const subProduct = subProductsData.find((sp) => sp.subProductId === type);
      setRateControls((prev) => ({
        ...prev,
        variableRate: subProduct?.operationCostVariable ?? null,
        fixedRate: subProduct?.operationCostFixed ?? null,
        variableDiscount: subProduct?.discountRateVariable ?? null,
        fixedDiscount: subProduct?.discountRateFixed ?? null,
      }));
    }
  }, [type, setRateControls, useConcentrationRates, data]);

  useEffect(() => {
    const performCalculation = () => {
      try {
        const calculationBreakdown = calculate({
          quotedAmount: amount,
          quotedCurrency,
          startDate,
          endDate,
          rates: rateControls,
        });
        setCalculationResult(calculationBreakdown);
      } catch (error) {
        if (!isCreating) {
          console.error(error);
          showAlert(dispatch, 'error', 'Error al calcular el costo de emisión');
          setCalculationResult(null);
        }
      }
    };

    performCalculation();
  }, [amount, startDate, endDate, quotedCurrency, rateControls]);

  const handleRateChange = (field: keyof RiskPremiumRates, value: number) => {
    setRateControls((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const totalPremium = calculationResult?.amount || 0;

  return (
    <Paper sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: '50%',
            p: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}
        >
          <CalculateIcon sx={{ fontSize: '1rem' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Cálculo Detallado
          </Typography>
          <Chip
            label="SGR"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        </Box>
        {canEditRates && (
          <IconButton
            size="small"
            onClick={() => setShowRateControls(!showRateControls)}
            sx={{ color: 'primary.main' }}
          >
            <SettingsIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        )}
        {totalPremium > 0 && (
          <Box sx={{ textAlign: 'right' }}>
            <Stack
              direction="row"
              alignItems="start"
              justifyContent="flex-end"
              spacing={0.75}
            >
              <Typography variant="h4" color="primary">
                {formatMoney(totalPremium)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CLP
              </Typography>
            </Stack>
          </Box>
        )}
      </Stack>

      <ReverseCalculationPanel
        show={canEditRates && showRateControls}
        rateControls={rateControls}
        setRateControls={setRateControls}
        amount={amount}
        startDate={startDate}
        endDate={endDate}
        quotedCurrency={quotedCurrency}
      />

      <RatesCollapse
        show={showRateControls}
        rateControls={rateControls}
        onRateChange={handleRateChange}
        subProductsData={data?.subProducts}
        creditLineStatus={data?.status}
        isLoadingCreditLine={isPending}
        type={type}
        creditLineId={data?.id}
      />

      <Box sx={{ textAlign: 'center', my: 2 }}>
        {(!amount || !startDate || !endDate || !quotedCurrency || !type) ?? (
          <Box>
            <WarningIcon
              sx={{ fontSize: '2rem', color: 'text.disabled', mb: 0.75 }}
            />
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                fontSize: '0.8rem',
                lineHeight: 1.2,
              }}
            >
              Complete todos los campos requeridos
            </Typography>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', mt: 0.25, fontSize: '0.7rem' }}
            >
              Monto, días, tipo de garantía y tasa de conversión
            </Typography>
          </Box>
        )}
      </Box>

      {/* Calculation Breakdown */}
      {calculationResult && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              backgroundColor: 'grey.50',
              p: 1.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Monto en CLP
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationResult.amountInCLP)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Variable Original
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.originalVariableRate.toFixed(2)}%
                  </Typography>
                </Stack>
              </Grid>
              {calculationResult.variableDiscount > 0 && (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Descuento Variable
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.main"
                    >
                      -{calculationResult.variableDiscount.toFixed(2)}%
                    </Typography>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Variable Efectiva
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.effectiveVariableRate.toFixed(2)}%
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Componente Variable
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationResult.variableComponent)} CLP
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Fija Original
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.originalFixedRate.toFixed(2)} UF
                  </Typography>
                </Stack>
              </Grid>
              {calculationResult.fixedDiscount > 0 && (
                <Grid item xs={12}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Descuento Fijo
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.main"
                    >
                      -{calculationResult.fixedDiscount.toFixed(2)}%
                    </Typography>
                  </Stack>
                </Grid>
              )}
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Tasa Fija Efectiva
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {calculationResult.effectiveFixedRate.toFixed(2)} UF
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    Componente Fijo
                  </Typography>
                  <Typography variant="caption" fontWeight={600}>
                    {formatMoney(calculationResult.fixedComponent)} CLP
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
      {canEditRates && (
        <AlertBanner
          severity="info"
          title="Información Importante"
          message="Las tasas mostradas pueden ser modificadas usando el botón de configuración."
        />
      )}
    </Paper>
  );
};

export default Calculator;
